import { useEffect, useState } from 'react';
import { User, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password.trim()
      });
      return { error };
    } catch (err) {
      console.error('Error in signIn:', err);
      return { error: new Error('Failed to sign in') as AuthError };
    }
  };

  const signUp = async (email: string, password: string): Promise<{ error: AuthError | null }> => {
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password: password.trim()
      });
      return { error };
    } catch (err) {
      console.error('Error in signUp:', err);
      return { error: new Error('Failed to sign up') as AuthError };
    }
  };

  const signOut = async (): Promise<{ error: AuthError | null }> => {
    try {
      // First check if we have a session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // If no session, just update local state
        setUser(null);
        return { error: null };
      }

      // If we have a session, sign out properly
      const { error } = await supabase.auth.signOut();
      
      if (!error) {
        setUser(null);
      }
      
      return { error };
    } catch (err) {
      console.error('Error in signOut:', err);
      return { error: new Error('Failed to sign out') as AuthError };
    }
  };

  return { user, loading, signIn, signUp, signOut };
}